/** Persistent audio preferences shared by capture, processing, and playback. */
export type NoiseSuppression = 'off' | 'browser' | 'rnnoise' | 'gtcrn'
export type VoiceMode = 'open' | 'activity' | 'ptt'
export type AudioQuality = 'speech' | 'studio' | 'music'
export interface AudioSettings {
  inputVolume: number
  outputVolume: number
  noiseSuppression: NoiseSuppression
  echoCancellation: boolean
  autoGainControl: boolean
  highPass: boolean
  humFilter: 'off' | '50' | '60'
  voiceClarity: number
  compressor: boolean
  limiter: boolean
  voiceMode: VoiceMode
  autoThreshold: boolean
  gateThreshold: number
  gateHold: number
  pttKey: string
  pttRelease: number
  quality: AudioQuality
  shareAudio: boolean
}
export interface ParticipantAudio { volume: number; muted: boolean }
export const DEFAULT_AUDIO: AudioSettings = {
  inputVolume: 100, outputVolume: 100, noiseSuppression: 'rnnoise',
  echoCancellation: true, autoGainControl: false, highPass: true, humFilter: 'off',
  voiceClarity: 2, compressor: true, limiter: true, voiceMode: 'open',
  autoThreshold: true, gateThreshold: -48, gateHold: 200,
  pttKey: 'Space', pttRelease: 120, quality: 'studio', shareAudio: true,
}
export function bounded(value: unknown, min: number, max: number, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback
}
export function normalizeAudio(value: unknown): AudioSettings {
  const s = value && typeof value === 'object' ? value as Partial<AudioSettings> : {}
  const out = { ...DEFAULT_AUDIO }
  for (const k of ['echoCancellation', 'autoGainControl', 'highPass', 'compressor', 'limiter', 'autoThreshold', 'shareAudio'] as const) {
    if (typeof s[k] === 'boolean') out[k] = s[k]!
  }
  out.inputVolume = bounded(s.inputVolume, 0, 200, out.inputVolume)
  out.outputVolume = bounded(s.outputVolume, 0, 200, out.outputVolume)
  out.voiceClarity = bounded(s.voiceClarity, -6, 6, out.voiceClarity)
  out.gateThreshold = bounded(s.gateThreshold, -80, -10, out.gateThreshold)
  out.gateHold = bounded(s.gateHold, 50, 1000, out.gateHold)
  out.pttRelease = bounded(s.pttRelease, 0, 1000, out.pttRelease)
  if (['off', 'browser', 'rnnoise', 'gtcrn'].includes(s.noiseSuppression!)) out.noiseSuppression = s.noiseSuppression!
  if (['open', 'activity', 'ptt'].includes(s.voiceMode!)) out.voiceMode = s.voiceMode!
  if (['off', '50', '60'].includes(s.humFilter!)) out.humFilter = s.humFilter!
  if (['speech', 'studio', 'music'].includes(s.quality!)) out.quality = s.quality!
  if (typeof s.pttKey === 'string' && /^(Space|Key[A-Z]|Digit[0-9]|F([1-9]|1[0-2])|AltLeft|AltRight|ControlLeft|ControlRight|ShiftLeft|ShiftRight)$/.test(s.pttKey)) out.pttKey = s.pttKey
  return out
}
export function normalizeParticipants(value: unknown): Record<string, ParticipantAudio> {
  const result: Record<string, ParticipantAudio> = {}
  if (!value || typeof value !== 'object') return result
  for (const [id, item] of Object.entries(value)) {
    if (!id.startsWith('@') || !item || typeof item !== 'object') continue
    const p = item as Partial<ParticipantAudio>
    result[id] = { volume: bounded(p.volume, 0, 200, 100), muted: p.muted === true }
  }
  return result
}
export function playbackGain(settings: AudioSettings, person: ParticipantAudio | undefined, spatial = 1, deafened = false): number {
  if (deafened || person?.muted) return 0
  return bounded(settings.outputVolume, 0, 200, 100) / 100
    * bounded(person?.volume, 0, 200, 100) / 100 * bounded(spatial, 0, 1, 1)
}
export function captureOptions(settings: AudioSettings, deviceId = '') {
  return {
    ...(deviceId ? { deviceId } : {}), sampleRate: 48000,
    channelCount: settings.quality === 'music' ? 2 : 1,
    echoCancellation: settings.echoCancellation, autoGainControl: settings.autoGainControl,
    noiseSuppression: settings.noiseSuppression === 'browser',
  }
}
export function audioPublishOptions(settings: AudioSettings) {
  return {
    audioPreset: { maxBitrate: settings.quality === 'speech' ? 32000 : settings.quality === 'music' ? 128000 : 64000 },
    forceStereo: settings.quality === 'music', dtx: settings.quality !== 'music', red: true,
  }
}
